import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { organizationAPI } from '../services/api';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useBackgroundRemoval } from '../hooks/useBackgroundRemoval';
import './OrganizationSettings.css';

function OrganizationSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [formData, setFormData] = useState({
    orgName: '',
    orgAcronym: '',
    orgPrimaryColor: '#003366',
    orgSecondaryColor: '#FFFFFF',
    orgAllowTryouts: true,
    orgAllowPlayerHighlights: true,
    orgAllowSplitScreen: true,
    orgAllowBold: true,
    orgAllowMinimal: true,
    orgAllowPhotoFrame: true,
    orgAllowLogoUpload: false,
    orgAllowColorChange: false,
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [originalLogoImage, setOriginalLogoImage] = useState(null);
  const [cutoutLogoImage, setCutoutLogoImage] = useState(null);
  const [activeLogoVersion, setActiveLogoVersion] = useState('original');
  const { removeBg: removeLogoBg, isRemoving: isRemovingLogo, bgRemovalError: logoRemovalError } = useBackgroundRemoval();
  const [toastMessage, setToastMessage] = useState({ type: '', text: '' });
  // Helper function to show toast messages
  const showToast = (type, text, duration = 3000) => {
    setToastMessage({ type, text });
    setTimeout(() => setToastMessage({ type: '', text: '' }), duration);
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    const fetchOrgDefaults = async () => {
      try {
        const response = await organizationAPI.getDefaults();
        setFormData({
          orgName: response.data.orgName || '',
          orgAcronym: response.data.orgAcronym || '',
          orgPrimaryColor: response.data.orgPrimaryColor || '#003366',
          orgSecondaryColor: response.data.orgSecondaryColor || '#FFFFFF',
          orgAllowTryouts: response.data.orgAllowTryouts ?? true,
          orgAllowPlayerHighlights:
            response.data.orgAllowPlayerHighlights ?? true,
          orgAllowSplitScreen: response.data.orgAllowSplitScreen ?? true,
          orgAllowBold: response.data.orgAllowBold ?? true,
          orgAllowMinimal: response.data.orgAllowMinimal ?? true,
          orgAllowPhotoFrame: response.data.orgAllowPhotoFrame ?? true,
          orgAllowLogoUpload: response.data.orgAllowLogoUpload ?? false,
          orgAllowColorChange: response.data.orgAllowColorChange ?? false,
        });
        setLogoPreview(response.data.orgLogoUrl || null);
        setError('');
      } catch (err) {
        if (err.response?.status === 403) {
          showToast(
            'error',
            'Organization subscription required to access this page.'
          );
          setTimeout(() => navigate('/membership'), 3000);
        } else {
          setError('Failed to load organization settings');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchOrgDefaults();
  }, [navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleToggle = (fieldName) => {
    setFormData({
      ...formData,
      [fieldName]: !formData[fieldName],
    });
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Logo file must be less than 5MB');
        return;
      }

      const allowedTypes = [
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
        'image/svg+xml',
      ];
      if (!allowedTypes.includes(file.type)) {
        setError('Logo must be an image file (JPEG, PNG, GIF, or SVG)');
        return;
      }

      setError('');
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
        setOriginalLogoImage(reader.result);
        setCutoutLogoImage(null);
        setActiveLogoVersion('original');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setLogoPreview(null);
    setOriginalLogoImage(null);
    setCutoutLogoImage(null);
    setActiveLogoVersion('original');
  };

  const handleRemoveLogoBg = async () => {
    if (!originalLogoImage) return;
    const result = await removeLogoBg(originalLogoImage);
    if (result) {
      const { blobUrl, toBase64 } = result;
      setCutoutLogoImage(blobUrl);
      setLogoPreview(blobUrl);
      setActiveLogoVersion('cutout');
      toBase64.then((base64) => {
        setCutoutLogoImage(base64);
        setLogoPreview(base64);
      });
    }
  };

  const handleToggleLogoVersion = (version) => {
    setActiveLogoVersion(version);
    setLogoPreview(version === 'cutout' ? cutoutLogoImage : originalLogoImage);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccessMessage('');

    try {
      await organizationAPI.updateDefaults({
        orgName: formData.orgName,
        orgAcronym: formData.orgAcronym,
        orgLogoUrl: logoPreview,
        orgPrimaryColor: formData.orgPrimaryColor,
        orgSecondaryColor: formData.orgSecondaryColor,
        orgAllowTryouts: formData.orgAllowTryouts,
        orgAllowPlayerHighlights: formData.orgAllowPlayerHighlights,
        orgAllowSplitScreen: formData.orgAllowSplitScreen,
        orgAllowBold: formData.orgAllowBold,
        orgAllowMinimal: formData.orgAllowMinimal,
        orgAllowPhotoFrame: formData.orgAllowPhotoFrame,
        orgAllowLogoUpload: formData.orgAllowLogoUpload,
        orgAllowColorChange: formData.orgAllowColorChange,
      });

      setSuccessMessage('✅ Organization defaults saved successfully!');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save defaults');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="org-settings-container">
          <div className="loading">Loading organization settings...</div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="org-settings-container">
        <div className="org-settings-content">
          <div className="form-header">
            <button onClick={() => navigate('/')} className="back-button">
              ← Back to Home
            </button>
            <h1>Organization Settings</h1>
            <p>Set defaults for all your teams</p>
          </div>

          <div className="info-box">
            <strong>💡 How it works:</strong> These defaults will automatically
            apply to all new teams you create. You can still override colors or
            logos for individual teams during creation.
          </div>

          <form onSubmit={handleSubmit} className="org-settings-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="orgName">Organization Name *</label>
                <p className="field-description">
                  Full name of your organization
                </p>
                <input
                  id="orgName"
                  name="orgName"
                  type="text"
                  value={formData.orgName}
                  onChange={handleChange}
                  placeholder="Ex. Apex Ball Club Little League"
                  maxLength={100}
                  required
                  disabled={saving}
                />
              </div>

              <div className="form-group">
                <label htmlFor="orgAcronym">Organization Acronym *</label>
                <p className="field-description">
                  Short acronym (2-10 characters)
                </p>
                <input
                  id="orgAcronym"
                  name="orgAcronym"
                  type="text"
                  value={formData.orgAcronym}
                  onChange={handleChange}
                  placeholder="EX. ABCLL"
                  maxLength={10}
                  required
                  disabled={saving}
                  style={{ textTransform: 'uppercase' }}
                />
              </div>
            </div>
            <div className="form-group logo-upload-section">
              <label>Organization Logo</label>
              <p className="field-description">
                This logo will be used for all teams by default
              </p>
              <div className="logo-upload-container">
                {logoPreview ? (
                  <div className="logo-preview">
                    <img src={logoPreview} alt="Organization logo preview" />
                    {isRemovingLogo && (
                      <div style={{
                        position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderRadius: '8px',
                      }}>
                        <div style={{
                          width: '28px', height: '28px',
                          border: '3px solid rgba(255,255,255,0.3)',
                          borderTopColor: '#fff', borderRadius: '50%',
                          animation: 'bg-removal-spin 0.75s linear infinite',
                        }} />
                        <p style={{ color: '#fff', fontSize: '12px', margin: '8px 0 0' }}>Removing…</p>
                      </div>
                    )}
                    <div className="logo-actions">
                      <button
                        type="button"
                        className="btn-remove-logo"
                        onClick={handleRemoveLogo}
                        disabled={saving || isRemovingLogo}
                      >
                        ✕ Remove Logo
                      </button>
                    </div>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={handleRemoveLogoBg}
                      disabled={isRemovingLogo || saving}
                      style={{ marginTop: '8px', width: '100%' }}
                    >
                      {isRemovingLogo ? 'Removing background…' : '✂️ Remove Background'}
                    </button>
                    {logoRemovalError && (
                      <p style={{ color: '#e53e3e', fontSize: '12px', marginTop: '4px' }}>{logoRemovalError}</p>
                    )}
                    {originalLogoImage && cutoutLogoImage && (
                      <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                        <button
                          type="button"
                          className={activeLogoVersion === 'original' ? 'btn-primary' : 'btn-secondary'}
                          onClick={() => handleToggleLogoVersion('original')}
                          style={{ flex: 1 }}
                          disabled={isRemovingLogo}
                        >
                          Original
                        </button>
                        <button
                          type="button"
                          className={activeLogoVersion === 'cutout' ? 'btn-primary' : 'btn-secondary'}
                          onClick={() => handleToggleLogoVersion('cutout')}
                          style={{ flex: 1 }}
                          disabled={isRemovingLogo}
                        >
                          ✂️ Cutout
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <label
                    htmlFor="logo-upload"
                    className="logo-upload-placeholder"
                  >
                    <span className="upload-icon">📸</span>
                    <p>Upload Organization Logo</p>
                    <p className="upload-hint">
                      PNG, JPG, GIF or SVG (Max 5MB)
                    </p>
                  </label>
                )}
                <input
                  id="logo-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  className="logo-file-input"
                  disabled={saving}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="orgPrimaryColor">Default Primary Color</label>
                <p className="field-description">Main team color</p>
                <div className="color-input-wrapper">
                  <input
                    id="orgPrimaryColor"
                    name="orgPrimaryColor"
                    type="color"
                    value={formData.orgPrimaryColor}
                    onChange={handleChange}
                    disabled={saving}
                  />
                  <input
                    type="text"
                    value={formData.orgPrimaryColor}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        orgPrimaryColor: e.target.value,
                      })
                    }
                    placeholder="#003366"
                    className="color-text-input"
                    disabled={saving}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="orgSecondaryColor">
                  Default Secondary Color
                </label>
                <p className="field-description">Accent/text color</p>
                <div className="color-input-wrapper">
                  <input
                    id="orgSecondaryColor"
                    name="orgSecondaryColor"
                    type="color"
                    value={formData.orgSecondaryColor}
                    onChange={handleChange}
                    disabled={saving}
                  />
                  <input
                    type="text"
                    value={formData.orgSecondaryColor}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        orgSecondaryColor: e.target.value,
                      })
                    }
                    placeholder="#FFFFFF"
                    className="color-text-input"
                    disabled={saving}
                  />
                </div>
              </div>
            </div>

            <div className="color-preview">
              <div className="preview-label">Color Preview:</div>
              <div
                className="preview-swatch"
                style={{
                  backgroundColor: formData.orgPrimaryColor,
                  border: `4px solid ${formData.orgSecondaryColor}`,
                }}
              >
                <span style={{ color: formData.orgSecondaryColor }}>
                  Your Organization
                </span>
              </div>
            </div>

            {/* Template Access Controls */}
            <div className="controls-section">
              <h3>🎨 Template & Design Controls</h3>
              <p className="section-description">
                Control which templates and designs coaches can access. These
                settings apply to all organization teams by default. You can
                override settings for individual teams in Manage Coaches.
              </p>

              {/* Template Types */}
              <div className="control-group">
                <h4>Template Types</h4>
                <p className="control-note">
                  Game Preview and Game Recap are always available
                </p>

                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={formData.orgAllowTryouts}
                    onChange={() => handleToggle('orgAllowTryouts')}
                    disabled={saving}
                  />
                  <span className="toggle-slider"></span>
                  <span className="toggle-text">Allow Tryouts Templates</span>
                </label>

                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={formData.orgAllowPlayerHighlights}
                    onChange={() => handleToggle('orgAllowPlayerHighlights')}
                    disabled={saving}
                  />
                  <span className="toggle-slider"></span>
                  <span className="toggle-text">
                    Allow Player Spotlight Templates
                  </span>
                </label>
              </div>

              {/* Design Variations */}
              <div className="control-group">
                <h4>Design Variations</h4>
                <p className="control-note">
                  Classic design is always available
                </p>

                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={formData.orgAllowSplitScreen}
                    onChange={() => handleToggle('orgAllowSplitScreen')}
                    disabled={saving}
                  />
                  <span className="toggle-slider"></span>
                  <span className="toggle-text">Allow Split Screen Design</span>
                </label>

                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={formData.orgAllowBold}
                    onChange={() => handleToggle('orgAllowBold')}
                    disabled={saving}
                  />
                  <span className="toggle-slider"></span>
                  <span className="toggle-text">Allow Bold Design</span>
                </label>

                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={formData.orgAllowMinimal}
                    onChange={() => handleToggle('orgAllowMinimal')}
                    disabled={saving}
                  />
                  <span className="toggle-slider"></span>
                  <span className="toggle-text">Allow Minimal Design</span>
                </label>

                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={formData.orgAllowPhotoFrame}
                    onChange={() => handleToggle('orgAllowPhotoFrame')}
                    disabled={saving}
                  />
                  <span className="toggle-slider"></span>
                  <span className="toggle-text">Allow Photo Frame Design</span>
                </label>
              </div>

              {/* Team Customization */}
              <div className="control-group">
                <h4>Team Customization Permissions</h4>
                <p className="control-note">
                  Control what coaches can customize on their assigned teams
                </p>

                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={formData.orgAllowLogoUpload}
                    onChange={() => handleToggle('orgAllowLogoUpload')}
                    disabled={saving}
                  />
                  <span className="toggle-slider"></span>
                  <span className="toggle-text">
                    Allow coaches to upload team logos
                  </span>
                </label>

                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={formData.orgAllowColorChange}
                    onChange={() => handleToggle('orgAllowColorChange')}
                    disabled={saving}
                  />
                  <span className="toggle-slider"></span>
                  <span className="toggle-text">
                    Allow coaches to change team colors
                  </span>
                </label>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving...' : 'Save Defaults'}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                style={{ width: 'auto', flexShrink: 0 }}
                onClick={() => navigate('/')}
                disabled={saving}
              >
                Cancel
              </button>
            </div>
          </form>
          {/* Toast Notifications */}
          {error && <div className="toast-message toast-error">{error}</div>}
          {successMessage && (
            <div className="toast-message toast-success">{successMessage}</div>
          )}
        </div>
      </div>
      {/* Toast Notifications */}
      {toastMessage.text && toastMessage.type === 'success' && (
        <div className="toast-message toast-success">{toastMessage.text}</div>
      )}
      {toastMessage.text && toastMessage.type === 'error' && (
        <div className="toast-message toast-error">{toastMessage.text}</div>
      )}
      <Footer />
    </>
  );
}

export default OrganizationSettings;
